import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {setURLParams} from 'BenchlingUtil';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import HelpTooltip from 'help/HelpTooltip';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Clipboard from 'ui-library/Clipboard';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';
import * as net from 'util/Net';

import {ShareLink, ShareLinkMetadata} from '../ShareLinkConstants';
import {getOrCreateShareLinkMetadata, updateShareLinkMetadata} from './ShareMetadataDAO';

export const SHARE_LINK_MODAL_KEY = 'growth-experiment-share-link-modal';

enum LinkSharingStatus {
  ON = 'ON',
  OFF = 'OFF',
}
interface ModalProps {
  fileId: string;
  fileName: string;
  userId: string;
}
interface FormProps {
  fileId: string;
  link: ShareLink;
  metadata: ShareLinkMetadata;
  userId: string;
}

function getHelpText(): string {
  const userString = BenchlingConfig.ENABLE_SIGNED_OUT_SHARE_LINK_USAGE
    ? 'anyone with this link'
    : 'users on this tenant';

  return `Turning link sharing on provides read-only access to ${userString} until turned off.`;
}

function ShareLinkCreationForm(props: FormProps): JSX.Element {
  const {fileId, link, metadata, userId} = props;
  const [isActive, setIsActive] = useState(link.isActive);
  const [description, setDescription] = useState(metadata.description);
  const debouncedDescription = useDebounce(description, 500);

  useEffect(() => {
    try {
      updateShareLinkMetadata(fileId, debouncedDescription);
    } catch (e) {
      if (net.isServerRequestError(e)) {
        notifications.notifyError(e);
        return;
      }
      throw e;
    }
  }, [fileId, debouncedDescription]);

  const url = new URL(link.url).origin + setURLParams(link.url, {m: metadata.publicIdentifier});

  return (
    <>
      <div
        className={css(styles.toggleContainer)}
        {...getTestProps({component: 'ShareLinkCreationForm', element: 'root'})}
      >
        <span>Link Sharing</span>
        <HelpTooltip className={css(styles.tooltip)} text={getHelpText()} />
        <ToggleGroup
          options={[
            {id: LinkSharingStatus.ON, buttonProps: {text: 'ON'}},
            {id: LinkSharingStatus.OFF, buttonProps: {text: 'OFF'}},
          ]}
          size={ButtonSize.SMALL}
          selection={isActive ? LinkSharingStatus.ON : LinkSharingStatus.OFF}
          onChangeSelection={(val) => {
            const isActive = val === LinkSharingStatus.ON;
            analytics.track('share_links', isActive ? 'activated_share_link' : 'deactivated_share_link', {
              file_id: fileId,
              user_id: userId,
              has_metadata: true,
            });
            setIsActive(isActive);
            FileBrowserDAO.updateShareLink(link.shareIdentifier, {
              isActive,
            });
          }}
        />
      </div>
      <div>
        <textarea
          className={css(styles.textarea)}
          disabled={!isActive}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          value={description}
          placeholder="Add optional description"
        />
      </div>
      <div className={css(styles.urlContainer)}>
        <input
          className={css(styles.urlInput)}
          type="text"
          readOnly={true}
          disabled={!isActive}
          value={url}
          {...getTestProps({component: 'ShareLinkCreationModal', element: 'shareLinkInput'})}
        />
        <Clipboard
          value={url}
          notifyCopied={() => {
            analytics.track('share_links', 'copied_share_link', {
              file_id: fileId,
              user_id: userId,
              has_metadata: true,
            });
            notifications.success('Copied share link to clipboard.');
          }}
        >
          <Button
            className={css(styles.copyButton)}
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.SMALL}
            disabled={!isActive}
          >
            <BenchlingIcon icon="copy" /> Copy
          </Button>
        </Clipboard>
      </div>
    </>
  );
}

export default function ShareLinkCreationModal({fileId, fileName, userId}: ModalProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [link, setLink] = useState<ShareLink | null>(null);
  const [metadata, setMetadata] = useState<ShareLinkMetadata | null>(null);

  useEffect(() => {
    const onMount = async (): Promise<void> => {
      try {
        const link = await FileBrowserDAO.createShareLink(fileId);
        setLink(link!);
        const metadata = await getOrCreateShareLinkMetadata(fileId);
        setMetadata(metadata);
        setIsLoading(false);
      } catch (e) {
        if (net.isServerRequestError(e)) {
          notifications.notifyError(e);
          return;
        }
        throw e;
      }
    };
    onMount();
  }, [fileId]);

  return (
    <Modal
      title={`Share ${fileName}`}
      onRequestClose={() => {
        LegacyModalContainer.close(SHARE_LINK_MODAL_KEY);
      }}
    >
      {isLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <ShareLinkCreationForm fileId={fileId} link={link!} metadata={metadata!} userId={userId} />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  urlContainer: {
    position: 'relative',
    marginTop: '20px',
  },
  urlInput: {
    width: 'calc(100% - 65px)',
  },
  copyButton: {
    position: 'absolute',
    right: 0,
    borderRadius: '0 4px 4px 0',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  tooltip: {
    marginLeft: '10px',
    marginRight: '20px',
  },
  textarea: {
    height: '80px',
  },
});
